import './List.less';

import {
  ConfigProvider,
  List as BaseList,
  ListProps as BaseListProps,
} from 'antd';
import cn from 'classnames';
import React, {
  CSSProperties,
  FC,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getGutter, Gutter } from '../../utils';
import { Empty } from '../Empty/Empty';

export interface ListProps<T> {
  readonly dataSource?: BaseListProps<T>['dataSource'];
  readonly gap?: number;
  readonly id?: BaseListProps<T>['id'];
  readonly rowKey?: BaseListProps<T>['rowKey'];
  readonly padding?: Gutter;
  readonly children?: (item: T) => ReactNode | ReactNode[] | string;
  readonly className?: string;
  readonly transparent?: boolean;
  readonly height?: CSSProperties['height'];
  readonly maxHeight?: CSSProperties['height'];
  readonly emptyMessage?: ReactNode | string | ReactNode[];
  readonly emptyTemplate?: ReactNode | string | ReactNode[];
}

export const List: FC<ListProps<any>> = ({
  dataSource,
  gap,
  children,
  transparent,
  height,
  maxHeight,
  id,
  rowKey,
  className,
  padding,
  emptyMessage,
  emptyTemplate,
}) => {
  const ref = useRef<HTMLDivElement>();
  const [overlay, setOverlay] = useState<'bottom' | 'top'>('bottom');

  useEffect(() => {
    const container = ref.current?.querySelector('.ant-list');
    const handleScroll = (event: Event) => {
      const target = event.target as HTMLDivElement;

      if (target.scrollHeight - target.scrollTop === target.offsetHeight) {
        setOverlay('top');
      } else {
        setOverlay('bottom');
      }
    };

    container?.addEventListener('scroll', handleScroll);

    return () => container?.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        padding: getGutter(padding || 0),
      }}
      ref={ref as any}
    >
      {transparent && (height || maxHeight) && (
        <div
          className={cn('ant-list-overlay', {
            'ant-list-overlay--top': overlay === 'top',
            'ant-list-overlay--bottom': overlay === 'bottom',
          })}
        />
      )}
      <ConfigProvider
        renderEmpty={() =>
          emptyTemplate ? emptyTemplate : <Empty>{emptyMessage}</Empty>
        }
      >
        <BaseList
          renderItem={children}
          dataSource={dataSource}
          className={className}
          id={id}
          rowKey={rowKey}
          style={
            {
              height,
              maxHeight,
              overflow: !!height || !!maxHeight ? 'auto' : 'initial',
              '--item-gap': `calc(var(--spectrum-base-gutter) * ${gap})`,
            } as any
          }
        />
      </ConfigProvider>
    </div>
  );
};
